// libraries
import React, { useRef, useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import { useFormContext, RegisterOptions } from 'react-hook-form'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'

// svg
import { Warning } from '/react4xp/components/Svg/Ux/Ux'
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'

interface SelectProps {
    id: string
    name: string
    label?: string
    required?: boolean
    className?: string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    defaultValue: string
    options: Array<{
        value: string
        label: string
        disabled?: boolean
    }>
    errorMessages?: {
        required?: string
    }
    helperText?: string
    onOptionSelect?: (value: string) => void
}

export default function Select({
    id,
    name,
    label,
    required,
    className,
    onChange = () => {},
    defaultValue,
    options,
    errorMessages,
    helperText,
    onOptionSelect,
}: SelectProps) {
    
    const formContext = useFormContext()
    const { register, setValue, trigger, formState } = formContext || {}
    const errors = formState?.errors || {}

    let validations: RegisterOptions = {
        onChange: (e) => onChange(e)
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    const errorMessage = errors && errors[name]?.message

    // fake select dropdown
	const [isDropdownVisible, setIsDropdownVisible] = useState(false)
    const [selectedValue, setSelectedValue] = useState('')
    const buttonRef = useRef<HTMLButtonElement>(null)

    // bind a function on click to close the fake select dropdown if clicked outside
	const dropdownRef = useRef(null)

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownVisible(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

    // by default when you submit the form if there's an error on the input, it will focus on the input
    // but since this is a custom element, we don't want that, we want the focus on the invisible button instead
    useEffect(() => {
        if (errorMessage && !selectedValue && buttonRef.current) {
            buttonRef.current.focus()
        }
    }, [errorMessage, selectedValue])

    // add new state for search and currently focused option
    const [searchTerm, setSearchTerm] = useState('')
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)
    const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1)

    // add keyboard navigation handler
    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        // handle Enter key when an option is focused
        if (event.key === 'Enter' && focusedOptionIndex !== -1) {
            event.preventDefault()
            const selectedOption = options[focusedOptionIndex]
            if (selectedOption && !selectedOption.disabled) {
                setIsDropdownVisible(false)
                setValue(name, selectedOption.label)
                setSelectedValue(selectedOption.label)
                setFocusedOptionIndex(-1) // Clear focus
                trigger(name)
            }
            return
        }

        // Only handle printable characters
        if (event.key.length === 1) {
            event.preventDefault()
            
            // If dropdown isn't visible, show it
            if (!isDropdownVisible) {
                setIsDropdownVisible(true)
            }
            
            // Append to existing search term if within timeout
            const newSearchTerm = searchTimeout ? searchTerm + event.key : event.key
            setSearchTerm(newSearchTerm.toLowerCase())

            // Clear existing timeout
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }

            // Set new timeout to clear search term after 500ms
            const timeout = setTimeout(() => {
                setSearchTerm('')
                setSearchTimeout(null)
            }, 500)
            setSearchTimeout(timeout)

            // Find first matching option
            const matchingOptionIndex = options?.findIndex(option => 
                option.label.toLowerCase().startsWith(newSearchTerm.toLowerCase())
            )

            // If found a match, focus that option
            if (matchingOptionIndex !== -1) {
                setFocusedOptionIndex(matchingOptionIndex)
                
                // Scroll the option into view if needed
                const optionElement = document.querySelector(`.select-options button:nth-child(${matchingOptionIndex + 1})`)
                optionElement?.scrollIntoView({ block: 'nearest' })
            }
        }
    }, [searchTerm, searchTimeout, options, isDropdownVisible, focusedOptionIndex, setValue, name, trigger])

    // Add cleanup for timeout
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }
        }
    }, [searchTimeout])

    // listen for form reset event
    useEffect(() => {
        const handleFormReset = () => {
            setSelectedValue('')
            setIsDropdownVisible(false)
            setFocusedOptionIndex(-1)
            setSearchTerm('')
            if (setValue) {
                setValue(name, '')
            }
        }

        document.addEventListener('resetForm', handleFormReset)
        
        return () => {
            document.removeEventListener('resetForm', handleFormReset)
        }
    }, [name, setValue])

    return (
        <div className={clsx(
            'form-line',
            className,
            errors && errors[name] && 'form-line--error'
        )}>

            <label className='form-line__label text-md' htmlFor={id}>
                {label} {required && <span className='form-line__label__required text-sm'>(Required)</span>}
            </label>

            <div
                className={clsx(
                    'form-line__line-wrapper',
                    isDropdownVisible && 'active'
                )}
                ref={dropdownRef}
            >

                <div className='select-wrapper'>

                    <button
                        ref={buttonRef}
                        type='button'
                        className={clsx(
                            'form-line__toggle-select text-md',
                            selectedValue !== '' && 'invisible'
                        )}
                        onClick={() => setIsDropdownVisible(!isDropdownVisible)}
                        onBlur={() => selectedValue === '' && trigger(name)}
                        onKeyDown={handleKeyDown}
                    >
                        {defaultValue || 'Select an option'}
                    </button>

                    <input
                        type='text'
                        id={id}
                        name={name}
                        className='form-line__select text-md'
                        value={selectedValue}
                        disabled
                        onChange={onChange}
                        tabIndex={-1}
                        {...(register ? register(name || '', validations) : {})}
                    />

                    <div className='form-line__side-icon'>
                        <AngleDown />
                    </div>

                </div>

                <div className='select-options'>
                    {options?.map((item, i) => (
                        <button
                            key={i}
                            className={clsx(
                                'select-option',
                                focusedOptionIndex === i && 'focused'
                            )}
                            type='button'
                            onClick={() => {
                                setIsDropdownVisible(false)
                                setValue(name, item.label)
                                setSelectedValue(item.label)
                                setFocusedOptionIndex(-1)
                                trigger(name)
                                onOptionSelect?.(item.label)
                            }}
                            disabled={item.disabled}
                            tabIndex={isDropdownVisible ? 0 : -1}
                        >
                            {item.label}
                        </button>
                    ))}

                </div>

            </div>

            {isNonWhitespaceString(helperText) && (
                <div
                    className='form-line__help-text text-sm'
                    dangerouslySetInnerHTML={{ __html: helperText }}
                    suppressHydrationWarning
                />
            )}

            {errorMessage && (
                <p className='
                    form-line__error-message
                    form-line__error-message--input
                    labels-sm-regular
                '>
                    <Warning />
                    {typeof errorMessage === 'string' && errorMessage}
                </p>
            )}

        </div>
    )
}